<template>
    <LoadingPanel
        v-if="!isExportLayout"
        v-model:collapsed="collapsed"
        :headline="t('generators.conditionStatus.content.measurements.title')"
        collapsible
        class="condition-status-measurement-section"
    >
        <TabsComponent
            v-model="selectedTab"
            :tabs="tabs"
        />
    </LoadingPanel>

    <div v-if="isExportLayout" class="condition-status-measurement-section__export">
        <template v-for="tab in tabs" :key="tab.key">
            <component 
                :is="tab.tabComponent" 
                :selected-tab="tab.key" 
                :is-export-layout="isExportLayout"
            >
                {{ t(tab.titleKey ?? '') }}
            </component>
        </template>
    </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref, watch } from 'vue'
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import TabsComponent from '@/components/tabs/TabsComponent.vue'
import { TabItems } from '@/components/tabs/TabItems'
import { useI18n } from 'vue-i18n'
import { MeasurementType, MeasurementTypes } from '@/configuration/measurements/measurement-types'
import { useConditionStatusCollapseStore } from '@/store/generators/conditionStatusCollapseStore'
import { storeToRefs } from 'pinia'

const collapsed = defineModel<boolean>('collapsed', { default: true })

defineProps<{
    isExportLayout?: boolean
}>()

const conditionStatusCollapseStore = useConditionStatusCollapseStore()
const { collapsedState } = storeToRefs(conditionStatusCollapseStore)

const { t } = useI18n()
const defaultTab = MeasurementType.WindingResistance
const selectedTab = ref(collapsedState.value.measurements.selectedMeasurement ?? defaultTab)

const tabs: TabItems = [
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.WindingResistance)?.labelKey ?? '',
        key: MeasurementType.WindingResistance,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/WindingResistanceTab.vue')),
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.PoleImpedance)?.labelKey ?? '',
        key: MeasurementType.PoleImpedance,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/PoleImpedanceTab.vue')),
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.IsolationImpedanceStator)?.labelKey ?? '',
        key: MeasurementType.IsolationImpedanceStator,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/IsolationImpedanceStatorTab.vue')),
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.IsolationImpedanceRotor)?.labelKey ?? '',
        key: MeasurementType.IsolationImpedanceRotor,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/IsolationImpedanceRotorTab.vue')),
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.LossFactor)?.labelKey ?? '',
        key: MeasurementType.LossFactor,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/LossFactorTab.vue')),
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.PartialDischarge)?.labelKey ?? '',
        key: MeasurementType.PartialDischarge,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/PartialDischargeTab.vue')),
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.VoltageTesting)?.labelKey ?? '',
        key: MeasurementType.VoltageTesting,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/VoltageTestingTab.vue')),
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.InductionTesting)?.labelKey ?? '',
        key: MeasurementType.InductionTesting,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/InductionTestingTab.vue')),
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.HeatingTest)?.labelKey ?? '',
        key: MeasurementType.HeatingTest,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/HeatingTestTab.vue')),
    },
    {
        titleKey: MeasurementTypes.find((e) => e.value === MeasurementType.Vibrations)?.labelKey ?? '',
        key: MeasurementType.Vibrations,
        tabComponent: defineAsyncComponent(() => import('@/pages/generators/condition-status/sections/measurements/tabs/VibrationsTab.vue')),
    },
]

watch(selectedTab, (newValue) => {
    conditionStatusCollapseStore.collapsedState.measurements.selectedMeasurement = newValue
})
</script>

<style scoped lang="scss">
.condition-status-measurement-section {
    overflow: hidden;
    overflow-x: auto;

    &__export {
        display: flex;
        flex-direction: column;
        gap: $spacing-m;
        color: $pbl-text;
    }
}
</style>